import React from 'react'
import CalculatorInput from 'components/CalculatorInput'
import CalculatorNameInput from 'components/CalculatorInput/nameInput'
import CalculatorSelect from 'components/CalculatorSelect'
import CalculatorModel from './CalculatorModel'
import AngleOfViewContainer from 'components/AngleOfViewContainer'
import ResolutionContainer from 'components/ResolutionContainer'
import FocalLengthContainer from 'components/FocalLengthContainer'
import ImagerSizeContainer from 'components/ImagerSizeContainer'
import { CameraPicker } from 'components/MarkupDetails/ColorPicker'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { UNIT_OF_MEASUREMENT_OPTIONS, IMPERIAL, METRIC, RESOLUTIONS, IMAGER_SIZES, COPY_ICON_STACK } from 'helpers/globals'
import { connect } from 'react-redux'
import fovModeHelper from 'helpers/fovModeHelper'
import { updateCalculatorAction } from 'actions/calculatorActions'
import { updateFixedLensAction } from 'actions/multiImagerActions'
import injectSheet from 'react-jss'
import style from './style'
import { updateUiAction, updateUnitAction } from 'actions/uiActions'
import { changeCameraAction } from 'actions/cameraActions'
import { zoomToSingleCameraAction } from 'actions/mapActions'
import { checkOfflineLimitsAction } from 'actions/applicationActions'
import getCurrentCamera from 'helpers/cameras/getCurrentCamera'
import sortItems from 'helpers/sortItems'
import { lensModelsSelector } from 'selectors/components'

const mapStateToProps = ({ presentationValues, manufacturerSpecificCameraModels, cameras, cameraModels, ui: { isImperial }, ui }) => {
  const currentCamera = getCurrentCamera(cameras)
  return {
    presentationValues,
    currentCamera,
    isImperial,
    cameras,
    manufacturerSpecificCameraModels,
    resolution: `${currentCamera.horizontalResolution} x ${currentCamera.verticalResolution}`,
    cameraModelCount: ui.hideDiscontinuedModels ? cameraModels.filter(c => !c.discontinued).length : cameraModels.length,
    showBottomInputRow: ui.showBottomInputRow,
    lensModels: currentCamera.imagerId ? lensModelsSelector({ cameras, cameraModels }) : []
  }
}

const focalLengthDisplay = (fl, aov) => aov === 360 ? '0.0' : fl

class GeneralPanelContainer extends React.Component {
  renderLenses = _ => {
    const { currentCamera: { lensIds }, cameras, changeCameraAction, zoomToSingleCameraAction, classes } = this.props
    const lenses = lensIds.map(lensId => cameras.find(c => c.id === lensId))

    return (
      <div className={classes.lensContainer}>
        <span className={classes.lensLabel}>Imagers</span>
        <div className={classes.lensWrapper}>
          {
            sortItems(lenses).map(lens => (
              <a onClick={_ => {
                  changeCameraAction(lens.id)
                  zoomToSingleCameraAction(lens)
                }}
                key={'lens' + lens.id}
              >
                {lens.name} <FontAwesomeIcon icon='arrow-right' className={classes.rightArrow} />
              </a>
            ))
          }
        </div>
      </div>
    )
  }

  render() {
    const { presentationValues: { fieldOfView, angleOfView, ppf, distance, focalLength }, currentCamera, resolution,
      cameraModelCount, showBottomInputRow, manufacturerSpecificCameraModels, lensModels, updateCalculatorAction, updateUnitAction, isImperial,
      updateFixedLensAction, updateUiAction, zoomToSingleCameraAction, checkOfflineLimitsAction, classes } = this.props
    const { id, locked, fovMode, name, imagerSize, isMultiImager, imagerId, isFixedImager, color } = currentCamera
    const callback = isFixedImager ? updateFixedLensAction : updateCalculatorAction

    return (
      <div className={classes.container}>
        <div className={classes.aLittlePadding}>
          <CalculatorNameInput
            currentCamera={currentCamera}
            label='Name'
            value={name}
            fnKey={'name'}
            callback={updateCalculatorAction}
            labelClass='narrowLabel'
            width='73%'
          />
          <div className={classes.cameraActions}>
            <a
              onClick={_ => isMultiImager || imagerId ? checkOfflineLimitsAction('copyMultiImager', [imagerId || id]) : checkOfflineLimitsAction('copyCamera', [id])}
              title='Duplicate Camera'
            >
              {COPY_ICON_STACK}
            </a>
            <a
              onClick={_ => zoomToSingleCameraAction(currentCamera)}
              title='Go To Camera'
            >
              <FontAwesomeIcon icon='map-marker-alt' />
            </a>
            <a
              onClick={_ => updateCalculatorAction(!locked, 'locked', id)}
              title={locked ? 'Unlock Camera' : 'Lock Camera'}
            >
              <FontAwesomeIcon icon={locked ? 'lock' : 'unlock'} />
            </a>
          </div>
        </div>
        <div className={classes.aLittlePadding}>
          <CalculatorModel
            uiAction={imagerId ? updateUiAction.bind(null, { modal: 'LensModelSelector' }) : updateUiAction.bind(null, { modal: 'CameraModelSelector' })}
            {...{ currentCamera, cameraModelCount, manufacturerSpecificCameraModels, lensModels }}
          />
        </div>
        <div className={classes.colorPicker}>
          <span>Color</span>
          <CameraPicker
            currentColor={color}
            updateColor={colorKey => updateCalculatorAction(colorKey, 'color', id)}
          />
        </div>
        { isMultiImager
          ? this.renderLenses()
          : <div className={classes.inputs}>
              <div className={classes.generalInputs}>
                <ResolutionContainer
                  currentCamera={currentCamera}
                  >
                  <CalculatorSelect
                    currentCamera={currentCamera}
                    label='Resolution'
                    titleTag='Horizontal Resolution'
                    selectedValue={resolution}
                    values={RESOLUTIONS}
                    fnKey={'resolutions'}
                    callback={callback}
                    labelClass='extraWideLabel'
                  />
                </ResolutionContainer>
                <CalculatorInput
                  currentCamera={currentCamera}
                  label={isImperial ? 'PPF' : 'PPM'}
                  titleTag={`Pixels per ${isImperial ? 'Foot' : 'Meter'}`}
                  value={ppf.value}
                  fnKey={'ppf'}
                  callback={callback}
                  labelClass='extraWideLabel'
                  rawValue={currentCamera.ppf}
                />
                <CalculatorSelect
                  currentCamera={currentCamera}
                  label='Unit'
                  titleTag='Unit of Measurement'
                  selectedValue={isImperial ? IMPERIAL : METRIC}
                  values={UNIT_OF_MEASUREMENT_OPTIONS}
                  fnKey={'isImperial'}
                  callback={(value, _, __) => updateUnitAction(value === IMPERIAL)}
                  id={id}
                  labelClass='extraWideLabel'
                />
                <div className={classes.bottomRow}>
                  <ImagerSizeContainer
                    statelessComponent={
                      <CalculatorSelect
                        currentCamera={currentCamera}
                        label='Imager'
                        titleTag='Imager Size'
                        selectedValue={imagerSize}
                        values={IMAGER_SIZES}
                        fnKey='imagerSize'
                        callback={callback}
                        labelClass='extraWideLabel'
                      />
                    }
                    currentCamera={currentCamera}
                  />
                </div>
              </div>

              <div className={classes.cameraInputs}>
                <CalculatorInput
                  currentCamera={currentCamera}
                  label={'Distance'}
                  value={distance.value}
                  fnKey={'distance'}
                  unit={isImperial ? 'ft' : 'm'}
                  callback={callback}
                  labelClass='extraWideLabel'
                  rawValue={currentCamera.distance}
                />
                <CalculatorInput
                  currentCamera={currentCamera}
                  label={'Width'}
                  titleTag='Horizontal Width'
                  value={fieldOfView.value}
                  fnKey={`${fovModeHelper(fovMode)}FieldOfView`}
                  unit={isImperial ? 'ft' : 'm'}
                  callback={callback}
                  labelClass='extraWideLabel'
                  rawValue={currentCamera[`${fovModeHelper(fovMode)}FieldOfView`]}
                />
                <AngleOfViewContainer
                  statelessComponent={
                    <CalculatorInput
                      currentCamera={currentCamera}
                      label='HAoV'
                      titleTag='Horizontal Angle of View'
                      value={angleOfView.value}
                      fnKey={`${fovModeHelper(fovMode)}AngleOfView`}
                      unit='°'
                      callback={callback}
                      labelClass='extraWideLabel'
                      bottomRowToggle
                      uiAction={updateUiAction.bind(null, { showBottomInputRow: !showBottomInputRow })}
                      rawValue={currentCamera[`${fovModeHelper(fovMode)}AngleOfView`]}
                    />
                  }
                  bottomRowToggle
                  currentCamera={currentCamera}
                  callback={callback}
                  uiAction={updateUiAction.bind(null, { showBottomInputRow: !showBottomInputRow })}
                />
                <div className={classes.bottomRow}>
                  <FocalLengthContainer
                    currentCamera={currentCamera}
                    >
                    <CalculatorInput
                      currentCamera={currentCamera}
                      label='Focal Length'
                      value={focalLengthDisplay(focalLength.value, currentCamera.horizontalAngleOfView)}
                      fnKey={'focalLength'}
                      unit={'mm'}
                      callback={callback}
                      labelClass='extraWideLabel'
                      rawValue={currentCamera.focalLength}
                    />
                  </FocalLengthContainer>
                </div>
              </div>
            </div>
        }
      </div>
    )
  }
}

export default connect(mapStateToProps, { updateCalculatorAction, updateFixedLensAction, updateUiAction, updateUnitAction, changeCameraAction,
  zoomToSingleCameraAction, checkOfflineLimitsAction })(injectSheet(style)(GeneralPanelContainer))



// WEBPACK FOOTER //
// ./src/components/GeneralPanelContainer/index.js